import { AiChatMessage } from "@app/projects/network-assessment/provider-hub/interfaces";
import { FC, JSX, useEffect, useReducer, useMemo, useRef } from "react";
import { Modal } from "@app/shared/Modals";
import {
  ColumnConfig,

  wafflesKeyColumnName,
  SortDirections
} from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { CancelButtonType } from "@app/shared/Modals/modalContent";
import Markdown from "react-markdown";
import {
  AIChatPanelProviderLink
} from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanelProviderLink";
import {
  useCellMarkdownWithCompleteProviderLinks
} from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useCellMarkdownWithCompleteProviderLinks";
import ChatbotService from "@app/projects/network-assessment/provider-hub/ai-assistant/chatbotService";
import { toast } from "react-toastify";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import {
  aiChatTableChartReducer, aiChatTableChartInitialState,
  AIChatTableChartData
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatTableChartReducer";
import {
  formatCell, parseCurrencyCell
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatTableCellFormatter";
import { formatDateCustom } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";

interface AIChatTableChartProps {
  message: AiChatMessage;
  threadId: string;
  projectRunConfigurationId: number;
}

// A date cell as the backend renders it. A DateTime column comes through the ambient en-US culture's
// ToString(), which always carries a time — "1/15/2026 8:30:45 AM"; the bare-date form covers a string
// column that already holds a date. Deliberately strict, because Date.parse is permissive enough to
// read a bare year or "3/4" as a date and these cells hold NPIs and ZIPs. .NET separates the AM/PM
// designator with U+202F rather than a space, so accept either.
const DATE_CELL_PATTERN = /^\d{1,2}\/\d{1,2}\/\d{4}( \d{1,2}:\d{2}(:\d{2})?[ \u202F](AM|PM))?$/;

interface FetchPageOverrides {
  page?: number;
  pageSize?: number;
  sortCol?: string | null;
  sortDir?: SortDirections;
  colMap?: Record<string, string>;
}

export const AIChatTableChart: FC<AIChatTableChartProps> = ({ message, threadId, projectRunConfigurationId }) => {
  // Waffles calculates sticky-header activation based on offset from the top of the page, but inside a modal
  // the relevant boundary is the modal header bottom, not the viewport top. handleScroll overrides this by
  // toggling the "ai-chat-table-chart__fix" class manually. At certain zoom levels, Chrome rounds sub-pixel
  // values inconsistently, which can leave the header in the wrong state. ZOOM_BUFFER introduces a 1px hysteresis gap to prevent that.
  const ZOOM_BUFFER = 1;
  const chatbotService = useMemo(() => new ChatbotService(), []);
  const aiResponseReviewService = useMemo(() => new AiResponseReviewService(), []);
  const getCellMarkdownWithCompleteProviderLinks = useCellMarkdownWithCompleteProviderLinks();

  const [state, dispatch] = useReducer(aiChatTableChartReducer, aiChatTableChartInitialState);
  const {
    modalOpen, isModalReady, paginatedData, paginatedHeaders,
    paginationPage, paginationPageSize, paginationTotalCount,
    isPaginationLoading, sortColumn, sortDirection, columnMap, resetToFirstPage,
    isDownloading,
  } = state;

  const isPaginationEnabled = !!message.dataIsTruncated && !!message.responseId && !!threadId;

  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Waffles has a "fake-table" div at the top of the table that sticks to the top of the screen when scrolling
  // down such that the header would otherwise leave the page; Waffles calculates when the div turns on (i.e., replaces the normal header)
  // based on an offset from the top of the page, but since we're using Waffles in a modal, so we've got to handle
  // turning on the fake-table div ourselves
  const handleScroll = () => {
    if (!tableContainerRef.current) {
      return;
    }
    const wafflesContainer = tableContainerRef.current.querySelector(".waffles-container");
    const modalHeader = document.querySelector(".ai-chat-table-chart__header");
    if (!wafflesContainer || !modalHeader) {
      return;
    }
    const distanceFromViewportTopToWafflesTop = wafflesContainer.getBoundingClientRect().top;
    const distanceFromViewportTopToModalHeaderBottom = modalHeader.getBoundingClientRect().bottom;
    const shouldHaveFixClass = distanceFromViewportTopToWafflesTop < distanceFromViewportTopToModalHeaderBottom - ZOOM_BUFFER;
    tableContainerRef.current.querySelector(".fake-table").classList.toggle("ai-chat-table-chart__fix", shouldHaveFixClass);
  };

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer || !isModalReady || !modalOpen) {
      return;
    }

    tableContainer.addEventListener('scroll', handleScroll);

    return () => {
      tableContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isModalReady, modalOpen]);

  const handleToggleModal = () => {
    dispatch({ payload: { isPaginationEnabled }, type: 'TOGGLE_MODAL' });
  };

  // To use Waffles inside the Modal and have it "just work" without lots of additional changes to Waffles itself,
  // we have to account for the 300 ms Modal fade-in animations. Otherwise, when Waffles goes to set the height of its
  // horizontal scroll areas, it'll miss rows that aren't yet rendered. The 300ms delay isn't particularly noticeable.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modalOpen) {
        fetchPage();
      }
    }, 310);

    return () => clearTimeout(timer);
  }, [modalOpen]);

  const fetchPage = async (overrides?: FetchPageOverrides) => {
    if (!isPaginationEnabled) {
      dispatch({ payload: modalOpen, type: 'SET_MODAL_READY' });
      return;
    }
    const newPage = overrides?.page ?? paginationPage;
    const newPageSize = overrides?.pageSize ?? paginationPageSize;
    const newSortColumn = overrides?.sortCol !== undefined ? overrides.sortCol : sortColumn;
    const newSortDirection = overrides?.sortDir ?? sortDirection;
    const colMap = overrides?.colMap ?? columnMap;

    dispatch({
      payload: { page: newPage, pageSize: newPageSize, sortColumn: newSortColumn, sortDirection: newSortDirection },
      type: 'PREPARE_FETCH',
    });
    try {
      // Map display column name to SQL column name for backend sorting
      const sqlSortColumn = newSortColumn && newSortDirection !== SortDirections.None
        ? colMap[newSortColumn] ?? null
        : null;
      const sqlSortDirection = newSortDirection === SortDirections.Asc ? 'asc'
        : newSortDirection === SortDirections.Desc ? 'desc'
          : null;

      const result = await chatbotService.getTableResults(
        message.responseId,
        threadId,
        projectRunConfigurationId,
        newPage,
        newPageSize,
        sqlSortColumn,
        sqlSortDirection
      );

      let fetchedHeaders: string[] = [];
      let newData: AIChatTableChartData[] = [];

      if (result.items.length > 0) {
        fetchedHeaders = result.items[0].split('|');
        const fetchedRows = result.items.slice(1);
        newData = fetchedRows.map((row, rowIndex) => {
          const cells = row.split('|');
          const rowData: AIChatTableChartData = {
            [wafflesKeyColumnName]: `${paginationPage}-${rowIndex}`
          };
          fetchedHeaders.forEach((header, colIndex) => {
            const cellValue = formatCell(result.columnFormats?.[colIndex], cells[colIndex] ?? '---');
            const cellWithFormattedLinks = getCellMarkdownWithCompleteProviderLinks(cellValue);
            rowData[header] = (
              <Markdown
                key={colIndex}
                components={{
                  a: ({ ...props }) => <AIChatPanelProviderLink {...props} />
                }}
              >
                {cellWithFormattedLinks}
              </Markdown>
            );
          });
          return rowData;
        });
      }

      dispatch({
        payload: {
          columnMap: result.columnMap ?? {},
          data: newData,
          headers: fetchedHeaders,
          totalCount: result.totalCount,
        },
        type: 'FETCH_SUCCESS',
      });
      dispatch({ payload: modalOpen, type: 'SET_MODAL_READY' });
    } catch {
      toast("Problem loading table data. Please try asking Quinn to recreate the table.", {
        containerId: "z-index-9999",
        type: 'error',
      });
      dispatch({ type: 'FETCH_FAILURE' });
    }
  };

  const handlePageChange = (pageNumber: number) => {
    if (resetToFirstPage) {
      // WafflesPagination is calling back to acknowledge the resetToFirstPage signal after a sort.
      // The fetch was already dispatched by customSort — just clear the flag, don't fetch again.
      dispatch({ payload: pageNumber, type: 'CHANGE_PAGE' });
      return;
    }
    fetchPage({ page: pageNumber });
  };

  const handlePageSizeChange = (pageSize: number) => {
    fetchPage({ page: 1, pageSize });
  };

  const handleDownloadChart = async () => {
    dispatch({ payload: true, type: 'SET_DOWNLOADING' });
    try {
      let documentUrl: string;
      if (isPaginationEnabled) {
        documentUrl = await chatbotService.downloadTableResultsCsv(message.responseId, threadId, projectRunConfigurationId);
      } else {
        documentUrl = await chatbotService.downloadCsvMessageContents(message);
      }
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = `quinn-table-${formatDateCustom(new Date(), 'yyyyMMDD_HHmmss')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      aiResponseReviewService.updateTableDownloaded(message.responseId, true).then();
    } catch {
      toast("Problem downloading table. Please try again.", {
        containerId: "z-index-9999",
        type: 'error',
      });
    } finally {
      dispatch({ payload: false, type: 'SET_DOWNLOADING' });
    }
  }

  if (!message.content || message.content.length < 1) return null;

  const { headers, rows, wafflesData } = useMemo(() => {
    const headers = message.content[0].split('|');
    const rows = message.content
      .slice(1, 51).map(row => row.split('|')
        .map((cell, index) => {
            const cellWithFormattedLinks = getCellMarkdownWithCompleteProviderLinks(formatCell(message.columnFormats?.[index], cell));
            return (
              <Markdown
                key={index}
                components={{
                  a: ({ ...props }) =>
                    <AIChatPanelProviderLink {...props} />
                }}
              >
                {cellWithFormattedLinks}
              </Markdown>
            );
          }
        ));
    const wafflesData: AIChatTableChartData[] = [];
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      wafflesData[rowIndex] = {
        [wafflesKeyColumnName]: rowIndex + ''
      };
      for (let headerIndex = 0; headerIndex < headers.length; headerIndex++) {
        wafflesData[rowIndex][headers[headerIndex]] = !!rows[rowIndex][headerIndex] ? rows[rowIndex][headerIndex] : '---';
      }
    }

    return { headers, rows, wafflesData };
  }, [message.content]);

  // Build columns for paginated modal data; we memoize this b/c Waffles checks equality
  const paginatedColumns: ColumnConfig = useMemo(() => {
    const cols: ColumnConfig = {};
    (isPaginationEnabled ? paginatedHeaders : headers).forEach((header) => {
      cols[header] = {
        sizeContentToFit: true,
        testId: `modal-${header.toLowerCase().replace(/\s+/g, '-')}`
      };
    });
    return cols;
  }, [isPaginationEnabled, paginatedHeaders, headers]);

  const customSort = (segment: AIChatTableChartData[], column: keyof AIChatTableChartData, sortDir: SortDirections): AIChatTableChartData[] => {
    if (isPaginationEnabled) {
      fetchPage({ page: 1, sortCol: column as string, sortDir });
      return paginatedData;
    }

    // Frontend-only sorting for non-paginated tables
    if (sortDir === SortDirections.None) {
      return wafflesData;
    }

    const multiplier = sortDir === SortDirections.Asc ? 1 : -1;
    return segment.sort((a, b) => {
      const aVal = getRowValue(a[column]);
      const bVal = getRowValue(b[column]);

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }

      return (String(aVal) > String(bVal) ? 1 : -1) * multiplier;
    });
  }

  const getRowValue = (value: string | JSX.Element): string | number => {
    let textValue: string;

    if (typeof value === "string") {
      textValue = value;
    } else {
      textValue = value.props.children;
    }

    textValue = textValue.trim();

    const currencyValue = parseCurrencyCell(textValue);
    if (currencyValue !== null) {
      return currencyValue;
    }

    const percentageMatch = textValue.match(/^(\d+\.?\d*)%$/);
    if (percentageMatch) {
      return parseFloat(percentageMatch[1]);
    }

    const decimalMatch = textValue.match(/^-?\d+\.\d+$/);
    if (decimalMatch) {
      return parseFloat(textValue);
    }

    const integerMatch = textValue.match(/^-?\d+$/);
    if (integerMatch) {
      return parseInt(textValue, 10);
    }

    const commaSeparatedMatch = textValue.match(/^-?\d{1,3}(,\d{3})*(\.\d+)?$/);
    if (commaSeparatedMatch) {
      return parseFloat(textValue.replace(/,/g, ''));
    }

    // Dates must sort chronologically, which a string comparison cannot do: "12/4/2025" sorts after
    // "1/15/2026", and a 12-hour clock puts "1:00 PM" before "11:00 AM". Returning a timestamp routes
    // these through customSort's numeric branch instead. The cell is a wall-clock reading already in
    // the user's zone (the backend converts it using the same X-Timezone this browser reported), so
    // parsing it as local time is both correct and monotonic. V8 tolerates the U+202F that .NET puts
    // before the designator, but it is normalized anyway rather than relying on every engine to.
    const dateCandidate = textValue.replace(/\u202F/g, ' ');
    if (DATE_CELL_PATTERN.test(dateCandidate)) {
      const timestamp = Date.parse(dateCandidate);
      if (!isNaN(timestamp)) {
        return timestamp;
      }
    }

    return textValue.toLowerCase();
  }

  const modalWafflesData = isPaginationEnabled ? paginatedData : wafflesData;

  return (
    <>
      <div className="ai-chat-panel__message__table-wrapper">
        <table>
          <thead>
          <tr>
            {headers.map((header, index) => (<th key={index}>{header}</th>))}
          </tr>
          </thead>
          <tbody>
          {rows.map((row, index) => (<tr key={index}>
            {row.map((cell, index) => (<td key={index}>{!!cell ? cell : '---'}</td>))}
          </tr>))}
          </tbody>
        </table>
      </div>
      <Modal closeModal={handleToggleModal} isVisible={modalOpen}
             modalHeight={70} modalWidth={70}
             content={{
               body: <div className="ai-chat-table-chart__container">
                 {isModalReady ? (
                   <>
                     <div className="ai-chat-table-chart__header">
                       {paginationTotalCount > 50000 ? <p>Download will be limited to first 50,000 rows.</p> : null}
                       <QESButton qesType="secondary"
                                  qesTestId="ai-chat-table-chart__download"
                                  disabled={isDownloading}
                                  onClick={handleDownloadChart}>
                         {isDownloading ? 'Downloading...' : 'Download Table'}
                       </QESButton>
                     </div>
                     <div className="ai-chat-table-chart__table" ref={tableContainerRef}>
                       {(isPaginationLoading && paginatedData.length === 0) ? (
                         <LoadingSpinner />
                       ) : (
                         <Waffles<AIChatTableChartData>
                           data={modalWafflesData}
                           testId="ai-chat-table-chart__table"
                           fullWidth={true}
                           customSort={customSort}
                           passThroughSorting={isPaginationEnabled}
                           columns={paginatedColumns}
                           loading={isPaginationLoading}
                           {...(isPaginationEnabled ? {
                             enablePagination: true,
                             paginationProps: {
                               changePage: handlePageChange,
                               enableBackendPagination: true,
                               handlePageSizeChange: handlePageSizeChange,
                               pageSizeOptions: [25, 50, 100],
                               recordsPerPage: paginationPageSize,
                               resetToFirstPage: resetToFirstPage,
                               showPageSizeSelector: true,
                               totalRecords: paginationTotalCount
                             }
                           } : {})}
                         />
                       )}
                     </div>
                   </>
                 ) : (<LoadingSpinner />)}
               </div>,
               cancelButtonText: "",
               cancelButtonType: CancelButtonType.TopRight,
               confirmButtonAction: () => {
               },
               confirmButtonText: "",
               title: "Quinn Table",
             }}
      />
      <QESButton qesTestId="" onClick={handleToggleModal} qesType="secondary">View Full Table</QESButton>
    </>
  );
}